import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Activity, CheckCircle, XCircle, Clock, Play, RefreshCw, Zap, AlertTriangle } from "lucide-react";

const STATUS_COLORS = {
  queued: "bg-blue-100 text-blue-700",
  running: "bg-yellow-100 text-yellow-700",
  success: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
};

const STATUS_ICONS = {
  queued: Clock,
  running: Play,
  success: CheckCircle,
  failed: XCircle,
};

function formatDuration(ms) {
  if (!ms) return "-";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export function TaskDashboardTab({ adminAxios }) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [billingCheck, setBillingCheck] = useState(false);
  const [filterStatus, setFilterStatus] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await adminAxios.get("/queue/dashboard");
      setDashboard(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [adminAxios]);

  useEffect(() => { fetchDashboard(); const i = setInterval(fetchDashboard, 10000); return () => clearInterval(i); }, [fetchDashboard]);

  const runTest = async () => {
    setTesting(true);
    try {
      await adminAxios.post("/queue/test");
      setTimeout(fetchDashboard, 2000);
    } catch { /* ignore */ }
    setTesting(false);
  };

  const runBillingCheck = async () => {
    setBillingCheck(true);
    try {
      await adminAxios.post("/queue/run-billing-check");
      setTimeout(fetchDashboard, 3000);
    } catch { /* ignore */ }
    setBillingCheck(false);
  };

  const stats = dashboard?.stats || {};
  const byTask = dashboard?.by_task || [];
  const recent = dashboard?.recent || [];
  const failed = dashboard?.failed || [];

  const displayTasks = filterStatus
    ? recent.filter(t => t.status === filterStatus)
    : recent;

  if (loading) return <p className="text-sm text-muted-foreground p-4">Carregando...</p>;

  return (
    <div className="space-y-4" data-testid="task-dashboard-tab">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {[
          { label: "Total", value: stats.total || 0, icon: Activity, color: "text-gray-700" },
          { label: "Na Fila", value: stats.queued || 0, icon: Clock, color: "text-blue-600" },
          { label: "Executando", value: stats.running || 0, icon: Play, color: "text-yellow-600" },
          { label: "Concluidas", value: stats.success || 0, icon: CheckCircle, color: "text-green-600" },
          { label: "Falhadas", value: stats.failed || 0, icon: XCircle, color: "text-red-600" },
          { label: "Tempo Medio", value: formatDuration(stats.avg_duration_ms), icon: Zap, color: "text-purple-600", isText: true },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="py-3 px-3 text-center">
              <s.icon className={`w-4 h-4 mx-auto mb-1 ${s.color}`} />
              <div className={`text-lg font-bold ${s.color}`} data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`}>
                {s.isText ? s.value : s.value}
              </div>
              <div className="text-[10px] text-muted-foreground">{s.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Actions */}
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" variant="outline" onClick={fetchDashboard} data-testid="refresh-dashboard-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Atualizar
        </Button>
        <Button size="sm" variant="outline" onClick={runTest} disabled={testing} data-testid="test-queue-btn">
          <Zap className="w-4 h-4 mr-1" /> {testing ? "Testando..." : "Testar Fila"}
        </Button>
        <Button size="sm" variant="outline" onClick={runBillingCheck} disabled={billingCheck} data-testid="billing-check-btn">
          <AlertTriangle className="w-4 h-4 mr-1" /> {billingCheck ? "Verificando..." : "Verificar Billing"}
        </Button>
      </div>

      {/* By Task Name Breakdown */}
      {byTask.length > 0 && (
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm">Por Tipo de Tarefa</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <div className="grid gap-2">
              {byTask.map((t) => (
                <div key={t.task_name} className="flex items-center gap-3 text-sm border-b pb-2 last:border-0" data-testid={`task-type-${t.task_name}`}>
                  <span className="flex-1 font-mono text-xs">{t.task_name.replace("task_", "")}</span>
                  <Badge variant="outline" className="text-[10px]">{t.total} total</Badge>
                  <Badge className="bg-green-100 text-green-700 text-[10px]">{t.success} ok</Badge>
                  {t.failed > 0 && <Badge className="bg-red-100 text-red-700 text-[10px]">{t.failed} fail</Badge>}
                  <span className="text-[10px] text-muted-foreground w-16 text-right">{formatDuration(t.avg_duration_ms)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Tasks */}
      <Card>
        <CardHeader className="py-3 px-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Tarefas Recentes</CardTitle>
            <div className="flex gap-1">
              <Button size="sm" variant={filterStatus === null ? "default" : "ghost"} className="h-6 text-[10px] px-2" onClick={() => setFilterStatus(null)}>Todas</Button>
              {["queued", "running", "success", "failed"].map(s => (
                <Button key={s} size="sm" variant={filterStatus === s ? "default" : "ghost"} className="h-6 text-[10px] px-2" onClick={() => setFilterStatus(s)}>
                  {s === "queued" ? "Fila" : s === "running" ? "Exec" : s === "success" ? "OK" : "Falha"}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          <ScrollArea className="max-h-[350px]">
            {displayTasks.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Nenhuma tarefa</p>
            ) : (
              <div className="space-y-1.5">
                {displayTasks.map((t) => {
                  const Icon = STATUS_ICONS[t.status] || Clock;
                  return (
                    <div key={t.job_id} className="flex items-center gap-2 text-xs border rounded-md p-2" data-testid={`task-${t.job_id}`}>
                      <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${STATUS_COLORS[t.status]?.split(" ")[1] || ""}`} />
                      <span className="font-mono flex-1 truncate">{t.task_name?.replace("task_", "")}</span>
                      <Badge className={`text-[9px] ${STATUS_COLORS[t.status]}`}>{t.status}</Badge>
                      <span className="text-muted-foreground w-14 text-right">{formatDuration(t.duration_ms)}</span>
                      <span className="text-muted-foreground w-28 text-right">
                        {t.enqueued_at ? new Date(t.enqueued_at).toLocaleString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "-"}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Failed Tasks (Dead Letter) */}
      {failed.length > 0 && (
        <Card className="border-red-200">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm text-red-600 flex items-center gap-2">
              <XCircle className="w-4 h-4" /> Tarefas Falhadas
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <ScrollArea className="max-h-[200px]">
              <div className="space-y-2">
                {failed.map((t) => (
                  <div key={t.job_id} className="border border-red-100 rounded-md p-2 text-xs" data-testid={`failed-task-${t.job_id}`}>
                    <div className="flex justify-between mb-1">
                      <span className="font-mono font-medium">{t.task_name?.replace("task_", "")}</span>
                      <span className="text-muted-foreground">{t.retries || 0} retries</span>
                    </div>
                    <p className="text-red-600 break-all">{t.error}</p>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
